import React, { useEffect, useMemo, useState } from 'react';
import { Button, ConfigProvider, Menu, Switch, Divider, Dropdown, MenuProps } from 'antd';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../store/auth/auth'; // Стор для управления авторизацией
import IconMainMenu from '../icons/iconMainMenu/iconMainMenu';
import IconLayoutMenu from '../icons/iconMainMenu/IconLayoutMenu';
import IconAllCamsMenu from '../icons/iconMainMenu/IconAllCamsMenu';
import IconMapMenu from '../icons/iconMainMenu/IconMapMenu';
import IconArchiveMenu from '../icons/iconMainMenu/IconArchiveMenu';
import IconUserMenu from '../icons/iconMainMenu/IconUserMenu';
import IconReportsMenu from '../icons/iconMainMenu/IconReportsMenu';
import IconSettingMenu from '../icons/iconMainMenu/IconSettingMenu';
import { AudioOutlined } from '@ant-design/icons';
import logo from './Logo placeholder.png'; // Логотип приложения
import './Style/style.css';

import { ReactComponent as ExitIcon } from 'utils/app/assets/icons/exit.svg';
import LanguageSegmented from '../../utils/modules/language/components/LangSwitcher/LanguageSegmented';
import { useWebSocketStore } from '../../store/websocket/store';

// Интерфейс для описания пунктов меню
interface MenuItem {
    label: string;
    key: string;
    icon: JSX.Element;
    className: string;
}

// Пропсы для основного компонента меню
interface MainMenuProps {
    onClick: (key: string) => void; // Коллбэк для обработки клика по меню
    currentMenuItem: string; // Текущий выбранный пункт меню
}

// Элементы меню
const items: MenuItem[] = [
    { label: 'Главное', key: 'main', icon: <IconMainMenu />, className: 'menu-item' },
    { label: 'Раскладки', key: 'layouts', icon: <IconLayoutMenu />, className: 'menu-item' },
    { label: 'Все камеры', key: 'allCams', icon: <IconAllCamsMenu />, className: 'menu-item' },
    { label: 'Карта', key: 'map', icon: <IconMapMenu />, className: 'menu-item' },
    { label: 'Архив', key: 'archive', icon: <IconArchiveMenu />, className: 'menu-item' },
    { label: 'Пользователи', key: 'users', icon: <IconUserMenu />, className: 'menu-item' },
    // { label: 'Отчёты', key: 'reports', icon: <IconReportsMenu />, className: 'menu-item' },
    // { label: 'Настройка', key: 'settings', icon: <IconSettingMenu />, className: 'menu-item' },
];

const MainMenu: React.FC<MainMenuProps> = ({ onClick, currentMenuItem }) => {
    const navigate = useNavigate(); // Навигация
    const { logout, user } = useAuthStore(); // Получение данных из состояния авторизации
    const [isModalOpen, setModalOpen] = useState(false); // Состояние модального окна
    const location = useLocation();
    const [modalWindow, setModalWindow] = useState<Window | null>(null); // Ссылка на новое окно
    const [initialized, setInitialized] = useState(false);
    const [dropdownOpen, setDropdownOpen] = useState(false); // Состояние dropdown меню
    const { isConnected } = useWebSocketStore();

    const menuConfig = useMemo(
        () => ({
            items: [
                { label: 'Главное', key: 'main', icon: <IconMainMenu />, path: '/main' },
                { label: 'Раскладки', key: 'layouts', icon: <IconLayoutMenu />, path: '/layouts' },
                {
                    label: 'Все камеры',
                    key: 'allCams',
                    icon: <IconAllCamsMenu />,
                    path: '/allcams',
                },
                {
                    label: 'Карта',
                    key: 'map',
                    icon: <IconMapMenu />,
                    className: 'menu-item',
                    path: '/geomap',
                },
                {
                    label: 'Архив',
                    key: 'archive',
                    icon: <IconArchiveMenu />,
                    className: 'menu-item',
                    path: '/archive',
                },
                {
                    label: 'Пользователи',
                    key: 'users',
                    icon: <IconUserMenu />,
                    className: 'menu-item',
                    path: '/users',
                },
                // {
                //     label: 'Отчёты',
                //     key: 'reports',
                //     icon: <IconReportsMenu />,
                //     className: 'menu-item',
                // },
                // {
                //     label: 'Настройка',
                //     key: 'settings',
                //     icon: <IconSettingMenu />,
                //     className: 'menu-item',
                // },
            ],
            // Группы путей для вложенных страниц
            pathGroups: [
                { pathStart: '/device', menuKey: 'allCams' },
                { pathStart: '/camera', menuKey: 'allCams' },
            ],
        }),
        [],
    );

    // 2. Определяем активный пункт синхронно
    const getActiveMenuKey = () => {
        // Проверяем точное совпадение
        const exactMatch = menuConfig.items.find((item) => item.path === location.pathname);
        if (exactMatch) return exactMatch.key;

        // Проверяем группы путей
        const groupMatch = menuConfig.pathGroups.find((group) =>
            location.pathname.startsWith(group.pathStart),
        );
        if (groupMatch) return groupMatch.menuKey;

        // Возвращаем сохранённое значение или значение по умолчанию
        return localStorage.getItem('selectedMenuItem') || 'main';
    };
    // 3. Используем сразу правильное значение
    const [currentMenuItemm, setCurrentMenuItemm] = useState<string>(getActiveMenuKey());

    // 4. Обновляем только при изменении пути
    useEffect(() => {
        const newActiveKey = getActiveMenuKey();
        if (newActiveKey !== currentMenuItem) {
            setCurrentMenuItemm(newActiveKey);
        }

        // Сохраняем только для основных путей
        if (menuConfig.items.some((item) => item.path === location.pathname)) {
            localStorage.setItem('selectedMenuItem', newActiveKey);
        }
    }, [location.pathname]);

    // 5. Обработчик клика
    const handleClick = ({ key }: { key: string }) => {
        const menuItem = menuConfig.items.find((item) => item.key === key);
        if (menuItem) {
            // @ts-ignore
            navigate(menuItem.path);
            // Не нужно вызывать setCurrentMenuItem - сработает эффект от изменения пути
            localStorage.setItem('selectedMenuItem', key);
        }
        onClick?.(key);
    };
    //
    // // Обработка кликов по пунктам меню
    // const handleClick = ({ key }: { key: string }) => {
    //     const routes: { [key: string]: string } = {
    //         main: '/main',
    //         layouts: '/layouts',
    //         allCams: '/allcams',
    //         map: '/map',
    //         archive: '/archive',
    //         users: '/users',
    //         reports: '/reports',
    //         settings: '/settings',
    //     };
    //     const path = routes[key];
    //     if (path) {
    //         navigate(path); // Перемещаем пользователя на выбранный маршрут
    //     }
    //     localStorage.setItem('selectedMenuItem', key);
    //     setCurrentMenuItemm(key);
    //
    //     onClick(key); // Уведомляем родительский компонент
    // };

    // Обработка выхода пользователя
    const handleLogout = () => {
        logout(); // Очистка состояния авторизации
        window.location.href = '/'; // Перенаправление на главную страницу
    };

    const dropdownMenuItems: MenuProps['items'] = [
        {
            key: '1',
            className: 'dropdown-menu-item-settings--info',
            label: (
                // <div className={'dropdown-menu-item-settings--info'}>
                //     <span className="dropdown-menu-item-settings title medium">
                //         {user.name.split(' ')[1]}
                //     </span>
                // </div>
                <span className="dropdown-menu-item-settings title medium">{user?.name}</span>
            ),
            // onClick: () => setEditable(user, 0),

            // disabled: true,
        },
        { key: '2', type: 'divider' },
        {
            key: '3',
            className: 'dropdown-menu-item-settings--lang-switcher',
            label: (
                <div className="dropdown-menu-item-settings ">
                    <LanguageSegmented className={'lang_switcher_btn lang_switcher_btn-menu'} />
                </div>
            ),
            // disabled: true,
        },
        // {
        //     key: '4',
        //     label: <ThemeSwitcher />,
        // },
        {
            key: '4',
            className: 'dropdown-menu-item-settings--logout',
            label: (
                <Button
                    className="button-base button-size-large button-type-text button-state-danger dropdown-menu-item-settings--logout-btn body medium-bold"
                    onClick={handleLogout}
                    style={{ marginTop: '8px' }}
                >
                    <ExitIcon width={14} height={10} className={'dropdown-menu-icon'} />
                    Выйти
                </Button>
            ),
            // danger: true,
        },
    ];

    // Меню пользователя
    const userMenu = (
        <div className="custom-user-menu">
            <div className="user-name">{user?.login}</div>
            <Divider />
            <div className="language-switch">
                <Switch checkedChildren="RU" unCheckedChildren="KZ" defaultChecked />
            </div>
            <Divider />
            <div className="logout-section" onClick={handleLogout}>
                <Button type="text" danger icon={<AudioOutlined />}>
                    Выйти
                </Button>
            </div>
        </div>
    );

    const openModal = () => {
        window.open('/pttDispatch', '_blank');
    };

    const handleDropdownOpenChange = (open: boolean) => {
        setDropdownOpen(open);
    };

    return (
        <>
            <div className="menu-container">
                <div className="logo">
                    <img className="img" src={logo} alt="Logo" />
                </div>
                <ConfigProvider>
                    <Menu
                        className="menu"
                        onClick={handleClick}
                        selectedKeys={[currentMenuItemm]}
                        mode="horizontal"
                        items={items}
                    />
                </ConfigProvider>
                <div className="user-actions">
                    <button className="microphone-button" onClick={openModal}>
                        <svg width="14" height="20" viewBox="0 0 14 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path fillRule="evenodd" clipRule="evenodd" d="M8.5 6H9.5V5H8.5V4L9.5 4C9.5 2.61929 8.38071 1.5 7 1.5C5.61929 1.5 4.5 2.61929 4.5 4L7 4V5L4.5 5V6L7 6V7L4.5 7V8L7 8V9H4.5C4.5 10.3807 5.61929 11.5 7 11.5C8.38071 11.5 9.5 10.3807 9.5 9H8.5V8H9.5V7H8.5V6ZM11 6V5V4C11 1.79086 9.20914 0 7 0C4.79086 0 3 1.79086 3 4V5V6V7V8V9C3 11.2091 4.79086 13 7 13C9.20914 13 11 11.2091 11 9V8V7V6ZM6 16C2.74629 15.5168 0 12.3881 0 9H2C2 11.8995 4.10051 14 7 14C9.8995 14 12 11.8995 12 9H14C14 12.3881 11.2537 15.5168 8 16V18H10V20H8H6H4V18H6V16Z" fill="white" />
                        </svg>
                    </button>
                    <span className={`status-dot active`}></span>
                    {/*<span className={`status-dot ${isConnected ? 'active' : 'inactive'}`}></span>*/}
                    <Dropdown
                        menu={{ items: dropdownMenuItems }}
                        overlayClassName={'dropdown-menu-overlay-settings'}
                        placement="bottomRight"
                        trigger={['click']}
                        className={'menu-dropdown'}
                        destroyPopupOnHide={false}
                        open={dropdownOpen}
                        onOpenChange={handleDropdownOpenChange}
                    >
                        <Button className="user-button">
                            <span className="user-initials">
                                {user?.login.charAt(0).toUpperCase()}
                            </span>
                            <span className="user-name">{user?.login}</span>
                        </Button>
                    </Dropdown>
                </div>
            </div>
        </>
    );
};

export default MainMenu;
